import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  Divider,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { styled } from "@mui/material/styles";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import Notification from "../../components/common/Notification";
import { useAppSelector } from "../../redux/hooks";
import Loader from "../../components/common/Loader";
import {
  createVideoSection,
  updateVideos,
  resetVideoSectionState,
  getAllVideos,
} from "../../redux/slices/videoSectionSlice";

const StyledFormContainer = styled(Box)({
  width: "100%",
  height: "100%",
  backgroundColor: "background.default",
});

const StyledFormSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  height: "100%",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const StyledTypography = styled(Typography)({
  fontFamily: "Poppins, sans-serif !important",
});

const Form = ({ onClose, editData: propEditData }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const location = useLocation();
  const initialData = location.state?.initialData;

  // Use videoSection slice instead of teamMember
  const { loading, error, success } = useAppSelector(
    (state) => state.videoSection
  );

  const [notification, setNotification] = useState(null);
  const [editData, setEditData] = useState(propEditData || initialData || null);
  const [formData, setFormData] = useState({
    videoName: "",
    youtubeUrl: "",
    status: "active",
    seoTitle: "",
    seoDescription: "",
    seoKeywords: "",
  });

  // Fetch video data if ID is present in URL and we don't have editData
  useEffect(() => {
    if (id && !editData) {
      // You would need to implement getVideoById action and API endpoint
      // For now, we'll use getAllVideos and filter the results
      dispatch(getAllVideos())
        .unwrap()
        .then((response) => {
          const video = response.data.find((video) => video._id === id);
          if (video) {
            setEditData(video);
          }
        })
        .catch((error) => {
          setNotification({
            type: "error",
            message: "Failed to fetch video details",
          });
        });
    }
  }, [id, editData, dispatch]);

  // Handle success state
  useEffect(() => {
    if (success) {
      setNotification({
        type: "success",
        message: editData
          ? "Video updated successfully!"
          : "Video created successfully!",
      });
      setTimeout(() => {
        setNotification(null);
        dispatch(resetVideoSectionState());
        navigate("/video-gallery");
      }, 2000);
    }
  }, [success, editData, dispatch, navigate]);

  // Handle error state
  useEffect(() => {
    if (error) {
      setNotification({
        type: "error",
        message:
          typeof error === "string"
            ? error
            : error?.message || "Something went wrong!",
      });
    }
  }, [error]);

  // Populate form when editing
  useEffect(() => {
    if (editData) {
      setFormData({
        videoName: editData.videoName || "",
        youtubeUrl: editData.youtubeUrl || "",
        status: editData.status || "active",
        seoTitle: editData.seoTitle || "",
        seoDescription: editData.seoDescription || "",
        seoKeywords: editData.seoKeywords || "",
      });
    }
  }, [editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "seoKeywords") {
      // Clean up keywords by removing extra spaces
      const cleanedKeywords = value
        .split(",")
        .map((keyword) => keyword.trim())
        .filter((keyword) => keyword)
        .join(",");

      setFormData((prev) => ({
        ...prev,
        [name]: cleanedKeywords,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Update the handleSubmit function
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Validate form data if needed
      if (!formData.videoName.trim()) {
        setNotification({
          type: "error",
          message: "Video name is required",
        });
        return;
      }

      if (!formData.youtubeUrl.trim()) {
        setNotification({
          type: "error",
          message: "YouTube URL is required",
        });
        return;
      }

      // Submit form
      if (editData) {
        await dispatch(
          updateVideos({
            id: editData._id,
            data: formData,
          })
        ).unwrap();
      } else {
        await dispatch(createVideoSection(formData)).unwrap();
      }

      // Success is handled by the success effect
    } catch (err) {
      console.error("Form submission error:", err);
      setNotification({
        type: "error",
        message: err.message || "Something went wrong!",
      });
    }
  };

  const handleCancel = () => {
    navigate("/video-gallery");
  };

  return (
    <StyledFormContainer className="poppins-font">
      {loading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            background: "rgba(255,255,255,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loader />
        </Box>
      )}

      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 3,
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          position: "sticky",
          top: 0,
          zIndex: 1100,
        }}
        className="poppins-font"
      >
        <IconButton
          onClick={() => navigate("/video-gallery")}
          sx={{
            mr: 2,
            bgcolor: "background.paper",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <StyledTypography variant="h5" component="h1" fontWeight="bold">
          {editData ? "Edit" : "Add New"} Video
        </StyledTypography>
      </Box>

      {/* Main Form Content */}
      <Box sx={{ p: 3 }}>
        <form onSubmit={handleSubmit} className="poppins-font">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Video Details Section */}
            <StyledFormSection>
              <Typography
                variant="h6"
                className="poppins-font"
                gutterBottom
                sx={{ mb: 3 }}
              >
                Video Details
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  fullWidth
                  label="Video Name *"
                  name="videoName"
                  value={formData.videoName}
                  onChange={handleChange}
                  required
                  className="poppins-font"
                  InputProps={{
                    className: "poppins-font",
                  }}
                  InputLabelProps={{
                    className: "poppins-font",
                  }}
                />
                <TextField
                  fullWidth
                  label="YouTube URL *"
                  name="youtubeUrl"
                  value={formData.youtubeUrl}
                  onChange={handleChange}
                  required
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="poppins-font"
                  InputProps={{
                    className: "poppins-font",
                  }}
                  InputLabelProps={{
                    className: "poppins-font",
                  }}
                />
                <FormControl fullWidth required>
                  <InputLabel className="poppins-font">Status *</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    label="Status *"
                    onChange={handleChange}
                    className="poppins-font"
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </StyledFormSection>

            {/* SEO Settings Section */}
            <StyledFormSection>
              <Typography className="poppins-font" variant="h6" gutterBottom>
                SEO Settings
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <TextField
                  fullWidth
                  label="SEO Title"
                  name="seoTitle"
                  value={formData.seoTitle}
                  onChange={handleChange}
                  className="poppins-font"
                  InputProps={{
                    className: "poppins-font",
                  }}
                  InputLabelProps={{
                    className: "poppins-font",
                  }}
                />
                <TextField
                  fullWidth
                  label="SEO Keywords"
                  name="seoKeywords"
                  value={formData.seoKeywords}
                  onChange={handleChange}
                  helperText="Enter keywords separated by commas"
                  className="poppins-font"
                  InputProps={{
                    className: "poppins-font",
                  }}
                  InputLabelProps={{
                    className: "poppins-font",
                  }}
                />
                <TextField
                  fullWidth
                  label="SEO Description"
                  name="seoDescription"
                  value={formData.seoDescription}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  className="poppins-font"
                  InputProps={{
                    className: "poppins-font",
                  }}
                  InputLabelProps={{
                    className: "poppins-font",
                  }}
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                {formData.seoKeywords.split(",").map((keyword, index) =>
                  keyword.trim() ? (
                    <Chip
                      key={index}
                      label={keyword.trim()}
                      onDelete={() => {
                        const newKeywords = formData.seoKeywords
                          .split(",")
                          .filter((_, i) => i !== index)
                          .join(",");
                        setFormData((prev) => ({
                          ...prev,
                          seoKeywords: newKeywords,
                        }));
                      }}
                      sx={{ m: 0.5 }}
                      className="poppins-font"
                    />
                  ) : null
                )}
              </Box>
            </StyledFormSection>
          </Box>

          {/* Form Actions */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              left: 0,
              right: 0,
              py: 2,
              mt: 3,
              borderTop: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              zIndex: 1000,
            }}
          >
            <Button
              onClick={handleCancel}
              variant="outlined"
              sx={{ minWidth: 120 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ minWidth: 120 }}
              className="poppins-font"
            >
              {loading ? "Saving..." : editData ? "Update" : "Submit"}
            </Button>
          </Box>
        </form>
      </Box>

      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </StyledFormContainer>
  );
};

export default Form;
